/**
 * ApplyHost — the flag-gated host mounted at `/apply` in place of the direct
 * `ApplicationFlow` (director-form-application-cutover spec, task 8.1).
 *
 * It performs exactly ONE decision, mirroring the proven
 * `application-surface-reconciliation` ternary precedent already in `App.tsx`
 * (`ApplicationReviewSurface` vs `AdminApplicationsPage`): it derives the
 * surface SOLELY from `isApplicationFormEnabled()` (Req 1.3).
 *
 *   - Flag OFF → render the UNMODIFIED `Legacy_Application_Flow` (Req 1.2, 1.7).
 *     The legacy flow lives inline (and unexported) in `App.tsx`, so it is
 *     supplied to this host as the `legacyFlow` element rather than imported —
 *     this keeps `ApplicationFlow` byte-for-byte intact and avoids an `App.tsx`
 *     ↔ `ApplyHost` circular import. Flipping the env value flips the surface
 *     with NO application source change (Req 1.5).
 *   - Flag ON → resolve the `competitionId` and `divisionKey` and render
 *     `ApplyRendererSurface` (which owns the load / not-available / error+retry
 *     lifecycle and delegates to the shared `ApplicationFormContainer`), routing
 *     all reads/writes through the `applicationFormClient` transport — never a
 *     legacy `ApplicantYearProfile` write (Req 1.1, 1.6, 9.4).
 *
 * `competitionId` resolution precedence (mirrors `ApplicationBuilderAdminRoute`
 * / `ApplicationReviewSurface`): an explicit `?competitionId=` query param (so a
 * Director can deep-link a specific competition), else
 * `resolveActiveCompetitionId(operativeYear)` from `useActiveSeason()`.
 *
 * `divisionKey` is the Contestant's current `Participation.divisionKey` when one
 * exists, else the empty string — the renderer treats `''` as "no division yet"
 * and the DIVISION bound control drives selection; the Division/Season bound
 * writes update it canonically.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 9.4
 */

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

import { isApplicationFormEnabled } from '@/config/featureFlags';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useAuth } from '@/hooks/useAuth';
import { useDerivedApplicationSeason } from '@/hooks/useDerivedApplicationSeason';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';

import { ApplyRendererSurface } from './ApplyRendererSurface';
import { IntakeWindowGate } from './IntakeWindowGate';
import { resolveApplyCompetition } from './resolveApplyCompetition';

const client = generateClient();

export interface ApplyHostProps {
  /**
   * The unmodified `Legacy_Application_Flow` element, rendered on the flag-off
   * path. Supplied by `App.tsx` as `<ApplicationFlow />` (task 9.1) so the
   * legacy surface stays byte-for-byte intact and `ApplicationFlow` need not be
   * exported (avoids an `App.tsx` ↔ `ApplyHost` circular import). Req 1.2, 1.7.
   */
  legacyFlow: React.ReactNode;
}

/**
 * Resolve the competition the Application_Surface scopes to, routing the
 * decision through the pure {@link resolveApplyCompetition} precedence
 * (apply-season-auto-derivation spec, task 5.3):
 *
 *   - **Applicant** → the Derived_Application_Season's seeded competition id
 *     (`resolveActiveCompetitionId(derivedYear)`), IGNORING any applicant-
 *     supplied `?competitionId=` param (R5.5). The season is auto-derived from
 *     the open intake window via {@link useDerivedApplicationSeason}; the
 *     applicant never picks it.
 *   - **Director + explicit `?competitionId=`** → the explicit competition,
 *     honored unchanged so a Director can still deep-link a specific
 *     competition (R8.3).
 *
 * `derivedCompetitionId` is `null` when Season_Derivation is unresolved; that
 * `null` propagates through `resolveApplyCompetition`. On the applicant path
 * this is unreachable here because the enclosing {@link IntakeWindowGate}
 * redirects to `/portal` before this hook mounts whenever the active-intake set
 * is empty (and a resolved active-intake set always yields a derived season),
 * so we coalesce the residual `null` to `''` to satisfy the surface's
 * `competitionId: string` contract without a spurious render.
 */
function useResolvedCompetitionId(): string {
  const [searchParams] = useSearchParams();
  const { isDirector, user } = useAuth();
  const { competitionYear: derivedYear } = useDerivedApplicationSeason();

  const explicitCompetitionId = searchParams.get('competitionId');
  const derivedCompetitionId =
    derivedYear != null
      ? resolveActiveCompetitionId(derivedYear, user?.activeTenantId)
      : null;

  return (
    resolveApplyCompetition({
      isDirector,
      explicitCompetitionId,
      derivedCompetitionId,
    }) ?? ''
  );
}

const LIST_APPLICANTS = /* GraphQL */ `
  query ApplyHostListApplicants {
    listApplicants(limit: 200) {
      items {
        id
        email
      }
    }
  }
`;

const LIST_YEAR_PROFILES = /* GraphQL */ `
  query ApplyHostListYearProfiles($applicantId: ID!) {
    listApplicantYearProfiles(applicantId: $applicantId) {
      competitionYear
      divisionKey
    }
  }
`;

interface ApplicantRow {
  id: string;
  email?: string | null;
}

interface YearProfileRow {
  competitionYear: number;
  divisionKey?: string | null;
}

/**
 * Resolve the signed-in Contestant's anchor `Applicant` id by email. Mirrors
 * the reliable `useApplicantDashboard` path (paginated `listApplicants`
 * matched by email) rather than `getApplicantByCognitoSub`, which has
 * unreliable userPool auth. Returns `null` when no record matches.
 */
async function resolveApplicantIdByEmail(email: string): Promise<string | null> {
  const res = (await client.graphql({
    query: LIST_APPLICANTS,
    authMode: 'userPool',
  })) as GraphQLResult<{ listApplicants: { items: ApplicantRow[] } }>;
  const items = res.data?.listApplicants?.items ?? [];
  const normalized = email.trim().toLowerCase();
  const match = items.find((a) => a.email?.trim().toLowerCase() === normalized);
  return match?.id ?? null;
}

/**
 * The Contestant's current `Participation.divisionKey` for the operative season,
 * or `''` when none exists yet (no record, no division assigned, or any read
 * failure). This is a best-effort optimization so a returning Contestant with an
 * assigned division loads the division-scoped form directly; it soft-fails to
 * `''` so it can NEVER block the Application_Surface — the DIVISION bound control
 * drives selection from `''` (Req 1.1 path; design "ApplyHost / divisionKey").
 */
function useCurrentDivisionKey(): string {
  const { user } = useAuth();
  const { operativeYear } = useActiveSeason();
  const [divisionKey, setDivisionKey] = React.useState<string>('');

  const email = user?.email;

  React.useEffect(() => {
    if (!email) {
      setDivisionKey('');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const applicantId = await resolveApplicantIdByEmail(email);
        if (cancelled) return;
        if (!applicantId) {
          setDivisionKey('');
          return;
        }

        const res = (await client.graphql({
          query: LIST_YEAR_PROFILES,
          variables: { applicantId },
          authMode: 'userPool',
        })) as GraphQLResult<{ listApplicantYearProfiles: YearProfileRow[] }>;
        if (cancelled) return;

        const profiles = res.data?.listApplicantYearProfiles ?? [];
        const current = profiles.find((p) => p.competitionYear === operativeYear);
        setDivisionKey(current?.divisionKey ?? '');
      } catch {
        // Soft-fail: an unresolved division is the empty selection, never a
        // broken apply surface.
        if (!cancelled) setDivisionKey('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, operativeYear]);

  return divisionKey;
}

/**
 * The flag-ON branch: gates on the intake window, then resolves
 * `competitionId` + `divisionKey` and delegates to `ApplyRendererSurface`.
 *
 * The `IntakeWindowGate` runs FIRST so that when no `Active_Intake_Season`
 * exists the surface redirects to `/portal` before the division-resolution
 * reads (`useResolvedCompetitionId` / `useCurrentDivisionKey`) ever run — no
 * form is rendered and no Participation is created on the redirect path
 * (Req 3.4, 6.6). The resolution hooks live in `ResolvedApplyRenderer`, which
 * the gate mounts only once at least one Active_Intake_Season is resolved.
 */
function ApplyRendererHost(): React.ReactElement {
  return (
    <IntakeWindowGate>
      <ResolvedApplyRenderer />
    </IntakeWindowGate>
  );
}

/**
 * The gated inner surface: resolves `competitionId` + `divisionKey` and renders
 * `ApplyRendererSurface`. Mounted by `IntakeWindowGate` only when an
 * Active_Intake_Season exists, so its reads never run on the redirect path.
 */
function ResolvedApplyRenderer(): React.ReactElement {
  const competitionId = useResolvedCompetitionId();
  const divisionKey = useCurrentDivisionKey();

  return <ApplyRendererSurface competitionId={competitionId} divisionKey={divisionKey} />;
}

export function ApplyHost({ legacyFlow }: ApplyHostProps): React.ReactElement {
  // Single source of selection: derive the surface SOLELY from the flag (Req
  // 1.3). Flipping the env value flips the surface with no source change (1.5).
  if (!isApplicationFormEnabled()) {
    // Flag OFF → the unmodified Legacy_Application_Flow (Req 1.2, 1.7).
    return <>{legacyFlow}</>;
  }

  // Flag ON → the director-form renderer path (Req 1.1, 1.6, 9.4). The surface
  // is mounted inside `PortalLayout`, whose `main` already provides the page
  // padding (and the NotificationBell chrome) and has NO fixed `TopNav` to
  // clear — so the surface renders directly with no extra shell. The former
  // `pt-20`/`py-10 lg:py-16` wrapper existed to clear the fixed nav of the old
  // `PublicLayout` mount; under `PortalLayout` it stacked as phantom headroom
  // above the page and is intentionally removed.
  return <ApplyRendererHost />;
}

export default ApplyHost;
